
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useQueueBasedSync } from './hooks/useQueueBasedSync';
import './i18n'; // Initialize i18n after React is loaded

// CRITICAL: Lazy load pages for maximum code splitting efficiency
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Signup = lazy(() => import("./pages/Signup"));
const EmailConfirmedPage = lazy(() => import("./pages/auth/EmailConfirmedPage"));
const CheckEmailPage = lazy(() => import("./pages/auth/CheckEmailPage"));
const Account = lazy(() => import("./pages/Account"));
const TopUp = lazy(() => import("./pages/TopUp"));
const PayPalTopUp = lazy(() => import("./pages/PayPalTopUp"));
const USDTTopUp = lazy(() => import("./pages/USDTTopUp"));
const Purchases = lazy(() => import("./pages/Purchases"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));
const Contact = lazy(() => import("./pages/Contact"));
const FacebookVerification = lazy(() => import("./pages/FacebookVerification"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Refund = lazy(() => import("./pages/Refund"));
const NotFound = lazy(() => import("./pages/NotFound"));
const OtpRental = lazy(() => import("./pages/OtpRental"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogAdminPage = lazy(() => import("./pages/BlogAdminPage"));
const BlogPostCreatePage = lazy(() => import("./pages/BlogPostCreatePage"));
const BlogPostEditPage = lazy(() => import("./pages/BlogPostEditPage"));
const TwoFAGenerator = lazy(() => import("./pages/TwoFAGenerator"));
const DataSplitter = lazy(() => import("./pages/DataSplitter"));
const FanpageMarketplace = lazy(() => import("./pages/FanpageMarketplace"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const FanpageOrderDetail = lazy(() => import("./pages/FanpageOrderDetail"));

// OPTIMIZED: Admin completely lazy-loaded - NEVER in main bundle
const Admin = lazy(() => import("./pages/Admin"));

// Import critical components that need to be available immediately
import Footer from "./components/Footer";
import ErrorBoundary from "./components/ErrorBoundary";
import TelegramButton from "./components/TelegramButton";
// --- THAY ĐỔI 1: Import ProtectedRoute ---
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from './context/NotificationContext';
import { ToastProvider } from './context/ToastContext';

import { ExchangeRateProvider } from './contexts/ExchangeRateContext';
import NotificationContainer from './components/notifications/NotificationContainer';
import UserNotificationDisplay from "@/components/notifications/UserNotificationDisplay";


import FacebookPixel from "./components/seo/FacebookPixel";
import SEOProvider from "./components/providers/SEOProvider";
import Logger from '@/utils/logger';

// Component that uses auth-dependent hooks
const AppContent = () => {
  // Initialize the queue-based sync system (inside AuthProvider)
  const { triggerPageLoadSync } = useQueueBasedSync();
  
  useEffect(() => {
    triggerPageLoadSync();
  }, [triggerPageLoadSync]);

  return (
    <Router>
      <div className="min-h-screen flex flex-col bg-background font-sans antialiased">
        <ErrorBoundary>
           <Suspense fallback={
             <div className="w-full h-screen flex items-center justify-center">
               <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
             </div>
           }>
            <Routes>
              {/* Main application routes - optimized loading */}
              <Route path="/*" element={
                <>
                  <UserNotificationDisplay />
                  <main className="flex-1">
                    <Routes>
                      <Route path="/" element={<Index />} />
                       <Route path="/login" element={<Login />} />
                       <Route path="/signup" element={<Signup />} />
                       <Route path="/auth/confirmed" element={<EmailConfirmedPage />} />
                       <Route path="/auth/check-email" element={<CheckEmailPage />} />
                      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                      <Route path="/top-up" element={<ProtectedRoute><TopUp /></ProtectedRoute>} />
                      <Route path="/top-up/paypal" element={<ProtectedRoute><PayPalTopUp /></ProtectedRoute>} />
                      <Route path="/top-up/usdt" element={<ProtectedRoute><USDTTopUp /></ProtectedRoute>} />
                      <Route path="/purchases" element={<ProtectedRoute><Purchases /></ProtectedRoute>} />
                      <Route path="/order-details/:orderId" element={<ProtectedRoute><OrderDetails /></ProtectedRoute>} />
                      <Route path="/order/:orderId" element={<ProtectedRoute><OrderDetails /></ProtectedRoute>} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/facebook-verification" element={<FacebookVerification />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/refund" element={<Refund />} />
                       <Route path="/blog" element={<BlogIndex />} />
                        <Route path="/blog/:slug" element={<BlogPost />} />
                       <Route path="/tools/2fa-generator" element={<TwoFAGenerator />} />
                       <Route path="/tools/data-splitter" element={<DataSplitter />} />
                       <Route path="/fanpage-marketplace" element={<FanpageMarketplace />} />
                       <Route path="/fanpage-order-details/:orderId" element={<ProtectedRoute><FanpageOrderDetail /></ProtectedRoute>} />
                         <Route 
                           path="/admin/blog" 
                           element={
                             <ProtectedRoute adminOnly={true}>
                               <BlogAdminPage />
                             </ProtectedRoute>
                           } 
                         />
                         <Route 
                           path="/admin/blog/new" 
                           element={
                             <ProtectedRoute adminOnly={true}>
                               <BlogPostCreatePage />
                             </ProtectedRoute>
                           } 
                         />
                         <Route 
                           path="/admin/blog/edit/:slug" 
                           element={
                             <ProtectedRoute adminOnly={true}>
                               <BlogPostEditPage />
                             </ProtectedRoute>
                           } 
                         />
                      
                      {/* --- THAY ĐỔI 2: Bọc Route bằng ProtectedRoute --- */}
                      <Route 
                        path="/otp-rental" 
                        element={
                          <ProtectedRoute>
                            <OtpRental />
                          </ProtectedRoute>
                        } 
                      />
                      
                      {/* CRITICAL: Admin routes completely isolated to prevent main bundle pollution */}
                      <Route 
                        path="/admin/*" 
                        element={
                          <ProtectedRoute adminOnly={true}>
                            <Admin />
                          </ProtectedRoute>
                        } 
                      />

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                  <Footer />
                  <TelegramButton />
                </>
              } />
            </Routes>
          </Suspense>
        </ErrorBoundary>
        <NotificationContainer />
        
      </div>
    </Router>
  );
};

function App() {
  
  // Performance optimized query client
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 10 * 60 * 1000, // 10 minutes
        gcTime: 15 * 60 * 1000, // 15 minutes
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        retry: (failureCount, error: any) => {
          if (error?.status >= 400 && error?.status < 500) {
            return false;
          }
          return failureCount < 1;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 15000),
      },
      mutations: {
        retry: 1,
        retryDelay: 1000,
      },
    },
  });
/*
  useEffect(() => {
    // Only run optimizations in browser environment
    if (typeof window !== 'undefined') {
      // Dynamically import and initialize optimizations
      Promise.all([
        import('./utils/bundleOptimization'),
        import('./utils/webVitals'),
        import('./utils/fontOptimization')
      ]).then(([bundleOpt, webVitals, fontOpt]) => {
        // Initialize optimizations after app is mounted
        bundleOpt.initializeOptimizations();
        webVitals.initWebVitals();
        webVitals.observePerformance();
        fontOpt.initializeFontOptimizations();
      }).catch(error => {
        Logger.warn('Failed to initialize optimizations:', error);
      });
    }
  }, []);
*/
  return (
    <QueryClientProvider client={queryClient}>
      <FacebookPixel>
          <SEOProvider>
            <ToastProvider>
            <NotificationProvider>
              <AuthProvider>
                <ExchangeRateProvider>
                  <AppContent />
                </ExchangeRateProvider>
              </AuthProvider>
            </NotificationProvider>
          </ToastProvider>
        </SEOProvider>
      </FacebookPixel>
    </QueryClientProvider>
  );
}

export default App;
